import React, { useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Badge, CircularProgress } from '@mui/material'
import { IconButton, StatusChip, Tooltip } from 'components/ds'
import { LoadClassificationIcon, getClassificationBorderClass } from './loadClassification'
import { resolveLoadStatusStyle } from '@/constants/load-status'
import { CanonicalLoad } from '@numeo/types-core'
import { cn } from 'utils/cn'
import { AgeDisplay } from 'components/shared/LoadsTable/LoadRow/AgeDisplay'
import { EmailIcon } from 'features/load-hub/icons'
import { ManualChatIcon, PhoneIcon } from 'components/icons/DispatchIcons'
import { parseContactEmail } from 'features/load-hub/utils/contact'
import { openDeadheadRoute } from 'features/load-hub/utils/location'
import { LocationData } from 'features/load-hub/types/filters'
import { buildCallHref, getCallProviderLabel, opensInNewTab } from 'features/load-hub/utils/callProvider'
import { useCallProvider } from 'features/load-hub/hooks/useCallProvider'
import { getFormattedPhone } from 'features/load-hub/utils/phone'
import { PhoneOutgoing, X } from 'lucide-react'
import { FactoringBadge } from './FactoringBadge'
import { PrefetchedFactoringBadge } from './PrefetchedFactoringBadge'
import { OutgoFactorableBadge } from './OutgoFactorableBadge'
import { FactoringProvider } from 'services/factoring'
import { useFactoringProviderStore } from 'hooks/factoringProviderStore'
import { isOutgoFactorable } from '@numeo/types'
import { LoadExpandedRow } from 'features/load-hub/components/LoadExpandedRow'
import { ErrorBoundary } from 'features/load-hub/components/ErrorBoundary'
import type { RowMeta } from './LoadsTable'
import { DEFAULT_LOAD_COLUMN_ORDER, LoadColumnId } from './types'
import { trackLoadInteraction } from 'features/load-hub/utils/sourceAnalytics'
import { setDisplayedLoadRank, trackCallHandoffResolved, trackLoadContactClicked } from 'features/load-hub/utils/loadAttributionAnalytics'

interface LoadRowProps {
    load: CanonicalLoad
    loadUid: string
    index: number
    isExpanded: boolean
    isSelected: boolean
    isViewed?: boolean
    /** When true, this load's email conversation is the one currently open in the
     *  right email rail (agentic-hub). Paints a blue tint + left border so the
     *  dispatcher can see which row the open conversation belongs to. */
    isEmailOpen?: boolean
    originLocation: string
    originLocationData: LocationData | null | undefined
    onSelectLoad: (id: number) => void
    onToggleExpand: (uid: string) => void
    onRowClick: (uid: string, event: React.MouseEvent) => void
    onLoadUpdate?: (updatedLoad: CanonicalLoad) => void
    onEmailClick?: (load: CanonicalLoad) => void
    analyticsSurface?: 'load_hub'
    /** Called when the contacted-broker (existing thread) email is clicked.
     * If provided, takes precedence over auto-expanding the row. Used by
     * agentic-hub to open the right email rail focused on this thread. */
    onOpenThread?: (load: CanonicalLoad, threadId: string) => void
    sendingEmail?: boolean
    errorEmail?: boolean
    emailThreadId?: string

    onCallClick?: (load: CanonicalLoad) => void
    hasPhone?: boolean
    isCalling?: boolean
    callError?: boolean
    callInProgress?: boolean
    hasBeenCalled?: boolean
    /** Render prop for the inline call controls (Listen / Join / End Call). When this
     *  returns a non-null node, it replaces the email + phone icons in the contact cell —
     *  used by agentic-hub during live or initiating calls. Returning null falls back to
     *  the email/phone icon path. */
    renderCallControls?: (loadUid: string) => React.ReactNode | null
    /** Mongo _id for the load — used by load-hub's inline email chat column. */
    loadMongoId?: string
    unreadCount?: number
    onResetUnread?: (loadUid: string) => void
    getSourceIcon: (sourceType: string) => React.ReactNode
    isNew?: boolean
    /** Optional per-row decorations from the consumer (agentic-hub). Absent in load-hub. */
    rowMeta?: RowMeta
    /** When true (and rowMeta.rowNumber is set), the row can be dragged — agentic-hub
     *  uses this to drop loads onto the Ask Numeo chat. Payload mime:
     *  `application/x-numeo-load-rows` = JSON array of row numbers. */
    draggableRows?: boolean
    /** Optional render prop for per-row action menu. Absent in load-hub. */
    rowActions?: (load: CanonicalLoad) => React.ReactNode
    /** Optional render prop for inline controls inside the Contact cell, right of the
     *  envelope icon. Used by agentic-hub for the block/favorite broker (star) toggle. */
    renderContactExtras?: (load: CanonicalLoad) => React.ReactNode
    /** Optional render prop that replaces the Rate column body. Used by agentic-hub
     *  to surface broker rate history via RateHistoryCell with a hover tooltip. When
     *  set, the default rate text is hidden and this output renders in its place. */
    renderRateCell?: (load: CanonicalLoad) => React.ReactNode
    /** Optional render prop that replaces the RPM column body. Agentic-hub uses this
     *  for the market-rate affordance (dotted-underline RPM number + tooltip showing
     *  the Market Intel lane rate). When set, the default RPM rendering is hidden and this
     *  output renders in its place. */
    renderRpmCell?: (load: CanonicalLoad) => React.ReactNode
    /** Optional render prop that replaces the Destination column body. Agentic-hub uses
     *  this for the Market Intel (destination reload-market) hover tooltip. When set, the
     *  default destination text is hidden and this output renders in its place. */
    renderDestinationCell?: (load: CanonicalLoad) => React.ReactNode
    /** Optional render prop that replaces the expanded-row body entirely. Agentic-hub
     *  passes LoadDetailPanel + email chat here; load-hub falls back to the shared
     *  LoadExpandedRow. The render fn receives the expanded load so the consumer can
     *  look up the matching CanonicalSearchLoad and build its own UI. */
    renderExpandedContent?: (load: CanonicalLoad) => React.ReactNode
    /** Mirror of LoadsTable.showRowNumber — adds a leading # cell. */
    showRowNumber?: boolean
    /** Optional pin toggle. When set, renders an inline pin button next to the row
     *  number; clicking flips the pinned state. Agentic-hub provides this; load-hub does not. */
    onPinRow?: (load: CanonicalLoad, pinned: boolean) => void
    /** Mirror of LoadsTable.showStatusColumn — adds a Status cell after Age. */
    showStatusColumn?: boolean
    /** Mirror of LoadsTable.showClassColumn — adds a leading colored-icon cell
     *  marking the load's AI fit-classification, and a matching row left-border. */
    showClassColumn?: boolean
    /** Mirror of LoadsTable.showStarColumn — adds a star (pin) toggle cell. */
    showStarColumn?: boolean
    /** Mirror of LoadsTable.showCloseColumn — adds a close/archive cell. */
    showCloseColumn?: boolean
    /** Called when the close button in the close column is clicked. */
    onCloseRow?: (load: CanonicalLoad) => void
    /** Order of the movable data columns (agentic-hub drag-to-reorder). Defaults to
     *  DEFAULT_LOAD_COLUMN_ORDER when omitted, so load-hub renders unchanged. */
    columnOrder?: LoadColumnId[]
}

const LoadRowImpl: React.FC<LoadRowProps> = ({
    load,
    loadUid,
    index,
    isExpanded,
    isSelected,
    isViewed = false,
    isEmailOpen = false,
    originLocation,
    originLocationData,
    onToggleExpand,
    onRowClick,
    onLoadUpdate,
    onEmailClick,
    analyticsSurface,
    onOpenThread,
    sendingEmail,
    errorEmail,
    emailThreadId,
    onCallClick,
    hasPhone,
    isCalling,
    callError,
    callInProgress,
    hasBeenCalled,
    renderCallControls,
    loadMongoId,
    unreadCount = 0,
    onResetUnread,
    getSourceIcon,
    isNew,
    rowMeta,
    draggableRows = false,
    rowActions,
    renderContactExtras,
    renderRateCell,
    renderRpmCell,
    renderDestinationCell,
    renderExpandedContent,
    showRowNumber = false,
    onPinRow,
    showStatusColumn = false,
    showClassColumn = false,
    showStarColumn = false,
    showCloseColumn = false,
    onCloseRow,
    columnOrder,
}) => {
    useEffect(() => {
        if (analyticsSurface === 'load_hub') setDisplayedLoadRank(load, index + 1)
    }, [analyticsSurface, index, load])
    const navigate = useNavigate()
    // Keep FactoringBadge mounted after first expand (avoid re-fetch on collapse/re-expand)
    const wasExpandedRef = useRef(false)
    if (isExpanded) wasExpandedRef.current = true

    // Outgo "$" factorability badge: only when Outgo is the selected provider and the
    // load is factorable (DAT flag on details.is_factorable). Display-only, no fetch.
    const factoringProvider = useFactoringProviderStore((s) => s.provider)
    const showOutgoBadge =
        factoringProvider === FactoringProvider.OUTGO && isOutgoFactorable((load.details as { is_factorable?: boolean | null } | null)?.is_factorable)

    // Reset unread count when row with email chat is expanded
    useEffect(() => {
        if (isExpanded && emailThreadId && unreadCount > 0) {
            onResetUnread?.(loadUid)
        }
    }, [isExpanded, emailThreadId, unreadCount, loadUid, onResetUnread])

    const isOdd = index % 2 === 1
    const isCancelled = !load.isActive
    const rawEmail = parseContactEmail(load.brokerContactEmail) || undefined
    // Handle comma-separated multiple emails
    const emailList = rawEmail
        ? rawEmail
              .split(/[,;]\s*/)
              .map((e) => e.trim())
              .filter(Boolean)
        : []
    const parsedEmail = emailList[0] || undefined
    const hasEmail = parsedEmail && parsedEmail.trim() !== ''
    const brokerName = load.brokerCompanyName || (load.sourceType === 'TQL' ? 'Total Quality Logistics' : '-')

    // Direct-dial affordance — opens the broker phone via the page-level call provider
    // (Call = tel:, Google Voice / RingCentral = web dialer in a new tab). Independent of
    // the AI call button (`onCallClick`), which places a call through the agent instead.
    const { provider: callProvider } = useCallProvider()
    const dialHref = load.brokerContactPhone ? buildCallHref(callProvider, load.brokerContactPhone) : null
    const dialPhoneDisplay = load.brokerContactPhone ? getFormattedPhone(load.brokerContactPhone)?.formatted : undefined

    // Telegram contact affordance, shown in the contact cell. One link, by priority:
    //   1. DM broker — brokerTelegramAccountLink (https://t.me/{username}), the broker's DM.
    //   2. View post — bookingUrl (https://t.me/c/{channel}/{message}), the source post.
    //   3. neither   — falls through to the "—" placeholder.
    // `bookingUrl` is a shared field other sources (DAT/Parade) populate with a generic
    // "book now" link, so each link requires a t.me URL before it's surfaced as Telegram.
    const telegramLink: { url: string; label: string } | null =
        load.sourceType === 'TELEGRAM'
            ? load.brokerTelegramAccountLink?.startsWith('https://t.me/')
                ? { url: load.brokerTelegramAccountLink, label: 'DM broker' }
                : load.bookingUrl?.startsWith('https://t.me/')
                  ? { url: load.bookingUrl, label: 'View post' }
                  : null
            : null

    // rowMeta decorations
    const isPinned = rowMeta?.isPinned ?? false
    const isBestFit = rowMeta?.bestFit ?? false
    const hasBrokerReplied = rowMeta?.brokerReplied ?? false
    const rowStatus = rowMeta?.status
    const hasPendingQuestion = rowMeta?.hasPendingQuestion ?? false
    const streamingState = rowMeta?.streamingState
    const statusReason = rowMeta?.statusReason
    const rowScore = rowMeta?.score
    const statusStyle = rowMeta?.statusStyle
    // AI fit-classification pill (Recommended/Matched/Potential/Duplicate/Not Matched),
    // rendered beside the workflow status pill. null for unscored/in-flight loads.
    const classification = rowMeta?.classification

    // Reference zebra: alternating #f7f7f8 / #eeeef0 via --lh-row-zebra-a/b tokens.
    // Active (expanded) + hover both use accent-soft.
    // bestFit adds a 3px green left border when not selected/expanded/new.
    // Classification left-border (recommended/matched/…) takes precedence over the
    // generic bestFit green border — it's the more specific signal. Gated on the
    // column flag so load-hub / non-classified surfaces keep their existing border.
    const classificationBorder = showClassColumn ? getClassificationBorderClass(classification) : null
    const rowBgClass = isSelected
        ? 'bg-[var(--lh-accent-soft)]'
        : isNew
          ? cn(
                // DAT-like "just arrived" cue: a bold green left border. Keep the normal zebra bg so
                // it reads as a subtle marker, not a full-row highlight.
                'border-l-[3px] border-l-green-500',
                isOdd ? 'bg-[var(--lh-row-zebra-b)] hover:bg-[var(--lh-accent-soft)]' : 'bg-[var(--lh-row-zebra-a)] hover:bg-[var(--lh-accent-soft)]'
            )
          : isExpanded
            ? 'bg-[var(--lh-accent-soft)]'
            : isEmailOpen
              ? 'border-l-[3px] border-l-[var(--lh-accent)] bg-[var(--lh-accent-soft)] hover:bg-[var(--lh-accent-soft-hover)]'
              : classificationBorder
                ? cn(
                      'border-l-[2px]',
                      classificationBorder,
                      isOdd ? 'bg-[var(--lh-row-zebra-b)] hover:bg-[var(--lh-accent-soft)]' : 'bg-[var(--lh-row-zebra-a)] hover:bg-[var(--lh-accent-soft)]'
                  )
                : isBestFit
                  ? cn(
                        'border-l-[3px] border-l-green-400/70',
                        isOdd ? 'bg-[var(--lh-row-zebra-b)] hover:bg-[var(--lh-accent-soft)]' : 'bg-[var(--lh-row-zebra-a)] hover:bg-[var(--lh-accent-soft)]'
                    )
                  : isOdd
                    ? 'bg-[var(--lh-row-zebra-b)] hover:bg-[var(--lh-accent-soft)]'
                    : 'bg-[var(--lh-row-zebra-a)] hover:bg-[var(--lh-accent-soft)]'

    // Sticky-column bg mirrors the row state but does NOT change on hover. The
    // hover-tint chain used to apply `group-hover:bg-[var(--lh-accent-soft)]` here,
    // which combined with the leftward shadow (shadow-[-4px_0_...]) created a visual
    // overlap that read as the Company column being clipped/painted-over by the X
    // column on hover. Keep the base/selected/new/expanded bg only — the shadow
    // alone is enough to signal the sticky boundary.
    const closeColBgClass = isSelected
        ? 'bg-[var(--lh-accent-soft)]'
        : isNew
          ? isOdd
              ? 'bg-[var(--lh-row-zebra-b)]'
              : 'bg-[var(--lh-row-zebra-a)]'
          : isExpanded
            ? 'bg-[var(--lh-accent-soft)]'
            : isEmailOpen
              ? 'bg-[var(--lh-accent-soft)]'
              : isOdd
                ? 'bg-[var(--lh-row-zebra-b)]'
                : 'bg-[var(--lh-row-zebra-a)]'

    // Viewed vs unviewed styling — reference lines 3978-3982
    const fgClass = isViewed ? 'text-[var(--lh-viewed-fg)]' : 'text-[var(--lh-row-fg)]'
    const mutedClass = isViewed ? 'text-[var(--lh-viewed-muted)]' : 'text-[var(--lh-row-muted)]'
    const weightClass = isViewed ? 'font-normal' : 'font-semibold'
    const weightBoldClass = isViewed ? 'font-normal' : 'font-bold'

    const handleDeadheadClick = useCallback((e: React.MouseEvent) => openDeadheadRoute(load, e, originLocation), [load, originLocation])

    const hasRowActions = rowActions !== undefined
    const hasPinColumn = !!onPinRow
    const extraLeadingCols = (showClassColumn ? 1 : 0) + (showRowNumber ? 1 : 0) + (hasPinColumn ? 1 : 0) + (showStatusColumn ? 1 : 0)
    const extraTrailingCols = (hasRowActions ? 1 : 0) + (showStarColumn ? 1 : 0) + (showCloseColumn ? 1 : 0)
    const colSpanTotal = 13 + extraLeadingCols + extraTrailingCols

    return (
        <React.Fragment>
            <tr
                className={cn('row-container group cursor-pointer transition-colors', rowBgClass, isCancelled && 'opacity-40')}
                data-load-id={load.id}
                data-row={loadUid}
                data-row-number={rowMeta?.rowNumber}
                data-row-listener-attached="true"
                draggable={draggableRows && rowMeta?.rowNumber != null}
                onDragStart={
                    draggableRows && rowMeta?.rowNumber != null
                        ? (e) => {
                              e.dataTransfer.setData('application/x-numeo-load-rows', JSON.stringify([rowMeta.rowNumber]))
                              e.dataTransfer.setData('text/plain', `#${rowMeta.rowNumber}`)
                              e.dataTransfer.effectAllowed = 'copy'
                          }
                        : undefined
                }
                onClick={(e) => onRowClick(loadUid, e)}
            >
                {/* AI fit-classification — leading colored-icon marker (far-left).
                    Renders nothing for unscored / in-flight loads. */}
                {showClassColumn && (
                    <td className="py-0.5 px-1 align-middle text-center">
                        <LoadClassificationIcon classification={classification} reason={statusReason} />
                    </td>
                )}
                {/* Row number — just the number, no inline pin. Pin lives in its
                    own dedicated column right after this one. On the agentic-hub
                    surface (draggableRows + rowNumber) the number is clickable —
                    it attaches the load to the Ask Numeo composer. data-ah-add-to-chat
                    exempts it from ChatPill's click-outside close. */}
                {showRowNumber && (
                    <td className="py-0.5 px-2 align-middle text-center">
                        {draggableRows && rowMeta?.rowNumber != null ? (
                            <Tooltip title="Add to chat" placement="top" arrow>
                                <button
                                    type="button"
                                    data-ah-add-to-chat
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        document.dispatchEvent(new CustomEvent('ah:add-load-to-chat', { detail: { rowNumber: rowMeta.rowNumber } }))
                                    }}
                                    aria-label={`Add load #${rowMeta.rowNumber} to chat`}
                                    className="inline-flex items-center justify-center min-w-[20px] h-5 rounded bg-transparent border-none p-0 cursor-pointer text-[11px] tabular-nums text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 hover:underline underline-offset-2 transition-colors"
                                >
                                    {rowMeta.rowNumber}
                                </button>
                            </Tooltip>
                        ) : (
                            <span className="text-[11px] tabular-nums text-slate-500 dark:text-slate-400">{rowMeta?.rowNumber ?? index + 1}</span>
                        )}
                    </td>
                )}

                {/* Dedicated pin column (agentic-hub only). Filled amber when pinned,
                    hover-only opacity gray when unpinned so unpinned rows stay clean. */}
                {onPinRow && (
                    <td className="py-0.5 px-1 align-middle text-center">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation()
                                onPinRow(load, !isPinned)
                            }}
                            title={isPinned ? 'Unpin load' : 'Pin load'}
                            aria-label={isPinned ? 'Unpin load' : 'Pin load'}
                            className={cn(
                                'inline-flex items-center justify-center w-5 h-5 rounded border-none bg-transparent p-0 cursor-pointer transition-colors',
                                isPinned
                                    ? 'text-amber-500'
                                    : 'text-slate-400 dark:text-slate-600 hover:text-slate-600 dark:hover:text-slate-300 opacity-0 group-hover:opacity-100'
                            )}
                        >
                            {isPinned ? (
                                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z" />
                                </svg>
                            ) : (
                                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z" />
                                </svg>
                            )}
                        </button>
                    </td>
                )}

                {(() => {
                    // Movable data columns keyed by id; emitted in `columnOrder` so the AI Hub can
                    // drag-reorder them. Leading/trailing structural cells stay fixed (outside this block).
                    const cells: Partial<Record<LoadColumnId, React.ReactNode>> = {
                        /* Age — reference col 1. Backend now backfills createdAt for sources
                            that don't expose posted time (BEON/NAVISPHERE/ARRIVE/ECHO/SPOT), so
                            AgeDisplay handles every source uniformly — no source-list guard. */
                        age: (
                            <td key="age" className="py-0.5 px-2 align-middle">
                                <div className="flex items-center gap-1">
                                    <span className={cn('text-[12px] font-normal', mutedClass)}>
                                        <AgeDisplay
                                            postedAt={
                                                load.createdAt ? (typeof load.createdAt === 'string' ? load.createdAt : load.createdAt.toISOString()) : null
                                            }
                                            // Sylectus carries a precise post datetime — show the exact clock time (ET) instead of a relative age.
                                            exactTime={load.sourceType === 'SYLECTUS'}
                                        />
                                    </span>
                                    {/* Inline status — fallback for callers without a dedicated Status column.
                                    Plain text + dot indicator, no chip frame, so the Age cell stays
                                    compact. Agentic-hub uses showStatusColumn instead. */}
                                    {rowStatus && !showStatusColumn && (
                                        <span className="inline-flex items-center gap-1 text-[10px] font-medium text-[var(--lh-fg-muted)] whitespace-nowrap flex-shrink-0">
                                            <span className="h-1.5 w-1.5 rounded-full bg-[var(--lh-fg-muted)]/60 flex-shrink-0" />
                                            {rowStatus.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                                        </span>
                                    )}
                                    {isCancelled && (
                                        <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-semibold text-red-700 dark:bg-red-900/30 dark:text-red-300 whitespace-nowrap flex-shrink-0">
                                            Cancelled
                                        </span>
                                    )}
                                </div>
                            </td>
                        ),
                        /* SRC — reference col 2 — 20×20 circular logo */
                        src: (
                            <td key="src" className="py-0.5 px-2 align-middle">
                                <div className="flex items-center h-[20px] [&_svg]:w-[20px] [&_svg]:h-[20px] [&_img]:w-[20px] [&_img]:h-[20px] [&_img]:rounded-full [&_img]:object-contain">
                                    {getSourceIcon(load.sourceType)}
                                </div>
                            </td>
                        ),
                        /* Rate — reference col 3. Ai-hub overrides via renderRateCell to surface
                            RateHistoryCell (rate + RPM stacked, with hover tooltip showing broker
                            rate history + market range). When that override is set the RPM column
                            below renders as a placeholder dash, because RateHistoryCell already
                            shows $/mi inline. */
                        rate: (
                            <td key="rate" className="py-0.5 px-2 align-middle">
                                <div className="flex items-center gap-1.5">
                                    {renderRateCell ? (
                                        renderRateCell(load)
                                    ) : load.rateAmountUsd != null && !isNaN(Number(load.rateAmountUsd)) ? (
                                        <span className={cn('text-[12px] tabular-nums whitespace-nowrap', weightBoldClass, fgClass)}>
                                            ${Number(load.rateAmountUsd).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                                        </span>
                                    ) : (
                                        <span className={cn('text-[12px]', mutedClass)}>—</span>
                                    )}
                                    {showOutgoBadge && <OutgoFactorableBadge />}
                                </div>
                            </td>
                        ),
                        /* RPM — reference col 4. Agentic-hub overrides via renderRpmCell to
                            surface the market-rate affordance (dotted-underline RPM + Market
                            Intel lane-rate tooltip). When overridden, the default RPM rendering below
                            is suppressed entirely; the consumer is expected to render both the
                            primary RPM value and the with-DH fallback line if it cares. */
                        rpm: (
                            <td key="rpm" className="py-0.5 px-2 align-middle">
                                {renderRpmCell ? (
                                    renderRpmCell(load)
                                ) : (
                                    <div className="flex flex-col leading-tight">
                                        {load.ratePerMileUsd != null && !isNaN(Number(load.ratePerMileUsd)) ? (
                                            <span className={cn('text-[12px] tabular-nums', weightBoldClass, fgClass)}>
                                                ${Number(load.ratePerMileUsd).toFixed(2)}
                                            </span>
                                        ) : load.rateAmountUsd && load.distanceMiles && Number(load.distanceMiles) > 0 ? (
                                            <span className={cn('text-[12px] tabular-nums', weightBoldClass, fgClass)}>
                                                ${(Number(load.rateAmountUsd) / Number(load.distanceMiles)).toFixed(2)}
                                            </span>
                                        ) : (
                                            <span className={cn('text-[12px]', mutedClass)}>—</span>
                                        )}
                                        {load.rateAmountUsd &&
                                            load.distanceMiles &&
                                            (load.details?.origin_deadhead_miles != null || load.details?.destination_deadhead_miles != null) &&
                                            (() => {
                                                const totalWithDH =
                                                    Number(load.distanceMiles) +
                                                    (load.details?.origin_deadhead_miles ?? 0) +
                                                    (load.details?.destination_deadhead_miles ?? 0)
                                                return totalWithDH > 0 ? (
                                                    <span className={cn('text-[10px] tabular-nums', mutedClass)}>
                                                        ${(Number(load.rateAmountUsd) / totalWithDH).toFixed(2)}
                                                    </span>
                                                ) : null
                                            })()}
                                    </div>
                                )}
                            </td>
                        ),
                        /* Miles — reference col 5 */
                        miles: (
                            <td key="miles" className="py-0.5 px-2 align-middle">
                                <span className={cn('text-[12px] tabular-nums whitespace-nowrap', fgClass)}>
                                    {load.distanceMiles ? `${Math.round(Number(load.distanceMiles)).toLocaleString()} mi` : '—'}
                                </span>
                            </td>
                        ),
                        /* Map action — reference uses /google-maps.svg (colored Google Maps logo) */
                        map: (
                            <td key="map" className="py-0.5 px-2 align-middle" onClick={(e) => e.stopPropagation()}>
                                <Tooltip title="View on Google Maps" placement="top" arrow>
                                    <button
                                        onClick={handleDeadheadClick}
                                        className="w-6 h-6 flex items-center justify-center rounded-md border-none bg-transparent cursor-pointer hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
                                    >
                                        <img src="/google-maps.svg" alt="Google Maps" className="w-[12px] h-[16px] object-contain" />
                                    </button>
                                </Tooltip>
                            </td>
                        ),
                        /* Origin — reference col 7 */
                        origin: (
                            <td key="origin" className="py-0.5 px-2 align-middle">
                                <span className={cn('text-[12px] whitespace-nowrap', weightClass, fgClass)}>
                                    {load.pickupCity}, {load.pickupStateProv}
                                </span>
                            </td>
                        ),
                        /* DH-O — reference col 8 */
                        dhO: (
                            <td key="dhO" className="py-0.5 px-2 align-middle">
                                <span className={cn('text-[12px] tabular-nums whitespace-nowrap', mutedClass)}>
                                    {load.details?.origin_deadhead_miles != null ? `${Math.round(Number(load.details.origin_deadhead_miles))} mi` : '—'}
                                </span>
                            </td>
                        ),
                        /* Destination — reference col 9. Agentic-hub overrides via renderDestinationCell
                            to surface the Market Intel (destination reload-market) hover tooltip. When
                            absent (load-hub), the default destination text renders unchanged. */
                        destination: (
                            <td key="destination" className="py-0.5 px-2 align-middle">
                                {renderDestinationCell ? (
                                    renderDestinationCell(load)
                                ) : (
                                    <span className={cn('text-[12px] whitespace-nowrap', weightClass, fgClass)}>
                                        {load.deliveryCity}, {load.deliveryStateProv}
                                    </span>
                                )}
                            </td>
                        ),
                        /* DH-D — reference col 10 */
                        dhD: (
                            <td key="dhD" className="py-0.5 px-2 align-middle">
                                <span className={cn('text-[12px] tabular-nums whitespace-nowrap', mutedClass)}>
                                    {load.details?.destination_deadhead_miles != null
                                        ? `${Math.round(Number(load.details.destination_deadhead_miles))} mi`
                                        : '—'}
                                </span>
                            </td>
                        ),
                        /* Equipment — reference col 11 (format: "Van • 53 ft • 10,050 lbs") */
                        equipment: (
                            <td key="equipment" className="py-0.5 px-2 align-middle">
                                <span className={cn('text-[12px] whitespace-nowrap overflow-hidden text-ellipsis block', fgClass)}>
                                    {load.equipmentType || 'V'}
                                    {load.equipmentLengthFt ? ` • ${load.equipmentLengthFt} ft` : ''}
                                    {load.weightLbs ? ` • ${load.weightLbs.toLocaleString()} lbs` : ''}
                                </span>
                            </td>
                        ),
                        /* Company — reference col 12 (always accent-colored, clickable look) */
                        company: (
                            <td key="company" className="py-0.5 px-2 align-middle" style={{ maxWidth: 150 }}>
                                <div className="flex items-center gap-1.5 min-w-0">
                                    {rowMeta?.factoring ? (
                                        <PrefetchedFactoringBadge {...rowMeta.factoring} />
                                    ) : (
                                        wasExpandedRef.current && (
                                            <ErrorBoundary fallback={() => null}>
                                                <FactoringBadge mcNumber={load.brokerMc} />
                                            </ErrorBoundary>
                                        )
                                    )}
                                    <Tooltip title={brokerName} placement="top" arrow>
                                        {load.brokerCompanyName ? (
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    navigate('/profiles/brokers', { state: { openBrokerEditCompanyName: load.brokerCompanyName } })
                                                }}
                                                className="text-[12px] text-[var(--lh-accent)] whitespace-nowrap overflow-hidden text-ellipsis bg-transparent border-0 p-0 m-0 cursor-pointer text-left hover:underline"
                                            >
                                                {brokerName}
                                            </button>
                                        ) : (
                                            <span className="text-[12px] text-[var(--lh-accent)] whitespace-nowrap overflow-hidden text-ellipsis">
                                                {brokerName}
                                            </span>
                                        )}
                                    </Tooltip>
                                    {hasBrokerReplied && (
                                        <Tooltip title="Broker replied" placement="top" arrow>
                                            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
                                        </Tooltip>
                                    )}
                                </div>
                            </td>
                        ),
                        /* Contact — reference col 13. Compact icon-only cell. Order: star
                            (favorite/block broker) first, then envelope (open thread / compose).
                            Email and phone strings live in the expanded row detail so the table
                            stays scannable. */
                        contact: (
                            <td key="contact" className="py-0.5 px-2 align-middle">
                                <div className="flex items-center gap-1 min-w-0">
                                    {renderContactExtras?.(load)}
                                    {/* When a call is initiating (HTTP in flight) OR live for this row,
                                    the consumer's `renderCallControls` returns the inline controls
                                    (Listen / Join / End Call) that replace email + phone icons.
                                    When null/undefined (or no call active), render the email/phone block. */}
                                    {(isCalling || callInProgress) && renderCallControls?.(loadUid) ? (
                                        renderCallControls(loadUid)
                                    ) : (
                                        <>
                                            {hasEmail ? (
                                                emailThreadId ? (
                                                    <Tooltip title={`Open email thread${parsedEmail ? ' — ' + parsedEmail : ''}`} placement="top" arrow>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                if (onOpenThread) onOpenThread(load, emailThreadId)
                                                                else onToggleExpand(loadUid)
                                                            }}
                                                            className="inline-flex items-center justify-center w-5 h-5 rounded text-[var(--lh-success)] hover:bg-emerald-50 dark:hover:bg-emerald-500/10 border-none bg-transparent cursor-pointer p-0 transition-colors"
                                                            aria-label="Open email thread"
                                                        >
                                                            <Badge
                                                                badgeContent={unreadCount > 0 ? unreadCount : null}
                                                                color="error"
                                                                className="[&_.MuiBadge-badge]:text-[9px] [&_.MuiBadge-badge]:h-3.5 [&_.MuiBadge-badge]:min-w-[14px] [&_.MuiBadge-badge]:px-0.5"
                                                                overlap="circular"
                                                            >
                                                                <ManualChatIcon color="green" className="w-3.5 h-3.5" />
                                                            </Badge>
                                                        </button>
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip
                                                        title={sendingEmail ? 'Sending…' : errorEmail ? 'Send failed' : parsedEmail || 'Compose email'}
                                                        placement="top"
                                                        arrow
                                                    >
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                if (!sendingEmail && !errorEmail) onEmailClick?.(load)
                                                            }}
                                                            disabled={sendingEmail || errorEmail}
                                                            aria-label="Compose email"
                                                            className={cn(
                                                                'inline-flex items-center justify-center w-5 h-5 rounded text-[var(--lh-accent)] hover:bg-blue-50 dark:hover:bg-blue-500/10 border-none bg-transparent transition-colors',
                                                                sendingEmail || errorEmail ? 'pointer-events-none' : 'cursor-pointer'
                                                            )}
                                                        >
                                                            {sendingEmail ? (
                                                                <span className="w-3.5 h-3.5 border-2 border-[var(--lh-accent)] border-t-transparent rounded-full animate-spin" />
                                                            ) : errorEmail ? (
                                                                <X className="w-3.5 h-3.5 text-[var(--lh-danger)]" strokeWidth={2.5} />
                                                            ) : (
                                                                <EmailIcon sx={{ width: '14px', height: '14px' }} />
                                                            )}
                                                        </button>
                                                    </Tooltip>
                                                )
                                            ) : null}
                                            {onCallClick && hasPhone && (
                                                <>
                                                    {hasEmail && <span className="h-3 w-px bg-[var(--lh-border)]" aria-hidden />}
                                                    <Tooltip
                                                        title={callError ? 'Call failed' : hasBeenCalled ? 'Call broker again' : 'Call broker'}
                                                        placement="top"
                                                        arrow
                                                    >
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                if (!callError) {
                                                                    if (analyticsSurface === 'load_hub')
                                                                        trackLoadContactClicked(load, 'phone', 'table_row', 'unknown')
                                                                    onCallClick(load)
                                                                }
                                                            }}
                                                            disabled={callError}
                                                            aria-label="Call broker"
                                                            className={cn(
                                                                'inline-flex items-center justify-center w-5 h-5 rounded border-none bg-transparent transition-colors',
                                                                hasBeenCalled
                                                                    ? 'text-[var(--lh-success)] hover:bg-emerald-50 dark:hover:bg-emerald-500/10'
                                                                    : 'text-[var(--lh-accent)] hover:bg-blue-50 dark:hover:bg-blue-500/10',
                                                                callError ? 'pointer-events-none' : 'cursor-pointer'
                                                            )}
                                                        >
                                                            {callError ? (
                                                                <X className="w-3.5 h-3.5 text-[var(--lh-danger)]" strokeWidth={2.5} />
                                                            ) : (
                                                                <PhoneIcon className="w-3.5 h-3.5" />
                                                            )}
                                                        </button>
                                                    </Tooltip>
                                                </>
                                            )}
                                            {dialHref && (
                                                <>
                                                    {(hasEmail || (onCallClick && hasPhone)) && <span className="h-3 w-px bg-[var(--lh-border)]" aria-hidden />}
                                                    <Tooltip
                                                        title={`Call ${dialPhoneDisplay || 'broker'}${callProvider !== 'call' ? ` via ${getCallProviderLabel(callProvider)}` : ''}`}
                                                        placement="top"
                                                        arrow
                                                    >
                                                        <a
                                                            href={dialHref}
                                                            target={opensInNewTab(callProvider) ? '_blank' : undefined}
                                                            rel={opensInNewTab(callProvider) ? 'noopener noreferrer' : undefined}
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                if (analyticsSurface === 'load_hub') {
                                                                    trackLoadInteraction(load.sourceType, 'call_clicked')
                                                                    const provider =
                                                                        callProvider === 'call' ||
                                                                        callProvider === 'googlevoice' ||
                                                                        callProvider === 'ringcentral'
                                                                            ? callProvider
                                                                            : 'unknown'
                                                                    const contactAttemptId = trackLoadContactClicked(load, 'phone', 'table_row', provider)
                                                                    trackCallHandoffResolved({
                                                                        load,
                                                                        contactAttemptId,
                                                                        callProvider: provider,
                                                                        outcome: provider === 'call' ? 'unobservable' : 'launched',
                                                                    })
                                                                }
                                                            }}
                                                            aria-label="Call broker phone"
                                                            className="inline-flex items-center justify-center w-5 h-5 rounded text-[var(--lh-accent)] hover:bg-blue-50 dark:hover:bg-blue-500/10 transition-colors no-underline"
                                                        >
                                                            <PhoneOutgoing className="w-3.5 h-3.5" />
                                                        </a>
                                                    </Tooltip>
                                                </>
                                            )}
                                            {telegramLink && (
                                                <>
                                                    {(hasEmail || (onCallClick && hasPhone) || dialHref) && (
                                                        <span className="h-3 w-px bg-[var(--lh-border)]" aria-hidden />
                                                    )}
                                                    <a
                                                        href={telegramLink.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            if (analyticsSurface === 'load_hub') {
                                                                trackLoadInteraction(load.sourceType, 'external_loadboard_opened')
                                                                trackLoadContactClicked(load, 'telegram', 'table_row')
                                                            }
                                                        }}
                                                        aria-label={telegramLink.label}
                                                        className="text-[11px] text-[var(--lh-accent)] hover:underline whitespace-nowrap"
                                                    >
                                                        {telegramLink.label}
                                                    </a>
                                                </>
                                            )}
                                            {!hasEmail && !(onCallClick && hasPhone) && !dialHref && !telegramLink && !renderContactExtras && (
                                                <span className={cn('text-[12px]', mutedClass)}>—</span>
                                            )}
                                        </>
                                    )}
                                </div>
                            </td>
                        ),
                    }
                    if (showStatusColumn) {
                        /* Status column (agentic-hub only) — single mutually-exclusive pill.
                            Priority order (mirrors main's LoadRowSummary):
                              1. streamingState.error → red "AI error" pill
                              2. streamingState.superseded → gray "AI stopped" pill (new-search cancel)
                              3. streamingState done + finalContent + status → status pill w/ checkmark
                              4. streamingState non-done → violet "Thinking..." / tool-name pill
                              5. statusStyle.isLoading → spinner (processing/ranking)
                              6. base status chip (simplified bucket via statusStyle from rowMeta)
                            Inline alongside the pill: a non-clickable amber bell for hasPendingQuestion. */
                        cells.status = (
                            // Locked width — streaming pills ("Updating Loads...") are wider than resting
                            // statuses; without a fixed width the column grows/shrinks per stream event and
                            // jitters the whole table. Floor is the pill's max-w-[140px] + padding.
                            <td key="status" className="py-0.5 px-2 align-middle w-[150px] min-w-[150px] max-w-[150px]">
                                <div className="flex items-center gap-1 min-w-0">
                                    {streamingState?.error ? (
                                        <span
                                            className="inline-flex w-fit max-w-[140px] items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400"
                                            title={streamingState.error}
                                            aria-label="AI agent failed"
                                        >
                                            <svg className="h-3 w-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    d="M12 9v3.75m0 3.75h.008v.008H12v-.008zM12 3l9 18H3l9-18z"
                                                />
                                            </svg>
                                            <span className="truncate">AI error</span>
                                        </span>
                                    ) : streamingState?.superseded ? (
                                        <span
                                            className="inline-flex w-fit max-w-[140px] items-center rounded-full px-2 py-0.5 text-[11px] font-medium bg-slate-100 dark:bg-slate-700/40 text-slate-600 dark:text-slate-300"
                                            title="Stopped by a new search"
                                            aria-label="AI stopped by new search"
                                        >
                                            <span className="truncate">AI stopped</span>
                                        </span>
                                    ) : streamingState &&
                                      streamingState.phase === 'done' &&
                                      streamingState.finalContent &&
                                      rowStatus &&
                                      statusStyle &&
                                      !statusStyle.isLoading ? (
                                        <span
                                            className={cn(
                                                'inline-flex w-fit max-w-[140px] items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium tracking-wide',
                                                statusStyle.bg,
                                                statusStyle.text
                                            )}
                                            aria-label="Processing complete"
                                        >
                                            <svg
                                                className={cn('h-3 w-3 flex-shrink-0', statusStyle.text)}
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                                strokeWidth={3}
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                                            </svg>
                                            <span className="truncate">{statusStyle.label || rowStatus}</span>
                                        </span>
                                    ) : streamingState && streamingState.phase !== 'done' ? (
                                        <span className="inline-flex w-fit max-w-[140px] items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium bg-violet-50 dark:bg-violet-500/10 text-violet-600 dark:text-violet-400">
                                            <span className="h-1.5 w-1.5 rounded-full bg-violet-500 animate-pulse flex-shrink-0" />
                                            <span className="truncate">
                                                {streamingState.phase === 'thinking'
                                                    ? 'Thinking...'
                                                    : streamingState.toolName
                                                      ? ({
                                                            send_email: 'Drafting Email...',
                                                            get_market_analysis: 'Checking Rate...',
                                                            search_loads: 'Searching...',
                                                        }[streamingState.toolName] ??
                                                        streamingState.toolName.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) + '...')
                                                      : 'Processing...'}
                                            </span>
                                        </span>
                                    ) : rowStatus && statusStyle?.isLoading ? (
                                        <Tooltip title={rowStatus.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())} placement="bottom" arrow>
                                            <span className="inline-flex items-center" aria-label={`Load ${rowStatus.replace(/_/g, ' ')}`}>
                                                <CircularProgress size={12} thickness={5} className="!text-slate-400 dark:!text-slate-500" />
                                            </span>
                                        </Tooltip>
                                    ) : rowStatus && statusStyle ? (
                                        (() => {
                                            const originalLabel = rowStatus.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
                                            const scorePart = rowScore != null ? `Score ${Math.round(rowScore * 100)}%` : null
                                            const tooltip = [originalLabel, statusReason, scorePart].filter(Boolean).join(' • ')
                                            return (
                                                <Tooltip title={tooltip} placement="bottom" arrow>
                                                    <span
                                                        className={cn(
                                                            'inline-flex w-fit max-w-[140px] items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium tracking-wide',
                                                            statusStyle.bg,
                                                            statusStyle.text
                                                        )}
                                                    >
                                                        <span className={cn('h-1 w-1 rounded-full flex-shrink-0', statusStyle.dot)} />
                                                        <span className="truncate">{statusStyle.label || rowStatus}</span>
                                                    </span>
                                                </Tooltip>
                                            )
                                        })()
                                    ) : rowStatus ? (
                                        <StatusChip {...resolveLoadStatusStyle(rowStatus)} reason={statusReason} />
                                    ) : (
                                        <span className="text-gray-300 dark:text-gray-600 text-xs">-</span>
                                    )}
                                    {hasPendingQuestion ? (
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                if (onOpenThread) onOpenThread(load, emailThreadId ?? '')
                                            }}
                                            title="Agent has a pending question — click to open"
                                            aria-label="Agent has a pending question"
                                            className="relative inline-flex items-center justify-center p-1 rounded-md border-none bg-transparent cursor-pointer text-amber-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-500/10 transition-colors flex-shrink-0"
                                        >
                                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    d="M10.05 4.575a1.575 1.575 0 10-3.15 0v3m3.15-3v-1.5a1.575 1.575 0 013.15 0v1.5m-3.15 0l.075 5.925m3.075.75V4.575m0 0a1.575 1.575 0 013.15 0V15M6.9 7.575a1.575 1.575 0 10-3.15 0v8.175a6.75 6.75 0 006.75 6.75h2.018a5.25 5.25 0 003.712-1.538l1.732-1.732a5.25 5.25 0 001.538-3.712l.003-2.024a.668.668 0 01.198-.471 1.575 1.575 0 10-2.228-2.228 3.818 3.818 0 00-1.12 2.687M6.9 7.575V12m6.27 4.318A4.49 4.49 0 0116.35 15m.002 0h-.002"
                                                />
                                            </svg>
                                            <span className="absolute top-0 right-0 h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
                                        </button>
                                    ) : null}
                                </div>
                            </td>
                        )
                    }
                    return (columnOrder ?? DEFAULT_LOAD_COLUMN_ORDER).map((id) => cells[id])
                })()}

                {/* Optional row actions — col 14 (agentic-hub only) */}
                {hasRowActions && (
                    <td className="py-0.5 px-2 align-middle" onClick={(e) => e.stopPropagation()}>
                        {rowActions(load)}
                    </td>
                )}

                {/* Star (pin) toggle column (agentic-hub only) */}
                {showStarColumn && (
                    <td className="py-0.5 px-2 align-middle" onClick={(e) => e.stopPropagation()}>
                        <button
                            type="button"
                            title={isPinned ? 'Unpin' : 'Pin'}
                            className={cn(
                                'flex items-center justify-center w-6 h-6 rounded transition-colors',
                                isPinned ? 'text-amber-500' : 'text-gray-300 dark:text-white/20 hover:text-amber-400'
                            )}
                        >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill={isPinned ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                            </svg>
                        </button>
                    </td>
                )}

                {/* Close (archive) column (agentic-hub only). Sticky-right so the ×
                    button stays visible even when the table scrolls horizontally
                    under a wide email rail. bg-inherit makes the sticky cell
                    pick up the row's zebra/hover/selected/new/best-fit bg
                    instead of always painting white — so the column blends with
                    whatever row it's in.

                    Shadow gating: on tinted rows (expanded / selected / new) the
                    leftward shadow painted a visible seam between the Company cell
                    and the × cell — the row's body is uniformly tinted, so any
                    darker edge reads as a column boundary. Skip the shadow on those
                    states; keep it on default zebra rows where horizontal-scroll
                    signaling is still useful (load-hub's table can scroll
                    horizontally; agentic-hub's flexes via .agentic-hub-results-table). */}
                {showCloseColumn && (
                    <td
                        className={cn(
                            'py-0.5 px-2 align-middle sticky right-0 z-[4] transition-colors',
                            !isExpanded &&
                                !isSelected &&
                                !isEmailOpen &&
                                'shadow-[-4px_0_8px_-2px_rgba(0,0,0,0.08)] dark:shadow-[-4px_0_8px_-2px_rgba(0,0,0,0.35)]',
                            closeColBgClass
                        )}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Archive action — no per-button hover bg. The row-hover
                            tint on the sticky td (inherited from the <tr>) provides
                            the surface signal; we just shift the × color slate → rose
                            on hover so the destructive intent is unambiguous. Avoids
                            the "pill floating in a darker frame" artifact when both
                            the row and the button paint their own hover surfaces. */}
                        <IconButton
                            size="md"
                            variant="ghost"
                            color="danger"
                            title="Archive"
                            onClick={() => onCloseRow?.(load)}
                            className="!bg-transparent hover:!bg-transparent !text-slate-500 dark:!text-slate-400 hover:!text-rose-600 dark:hover:!text-rose-400"
                            icon={
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            }
                        />
                    </td>
                )}
            </tr>

            {/* Expanded Row */}
            {isExpanded && (
                <tr>
                    <td colSpan={colSpanTotal} className="p-0 border-none">
                        <div className="relative">
                            <ErrorBoundary
                                fallback={(_err, retry) => (
                                    <div className="flex items-center justify-center gap-2 py-6 text-gray-400">
                                        <span className="text-xs">Failed to load details</span>
                                        <button onClick={retry} className="text-xs text-blue-500 hover:text-blue-600 underline">
                                            Retry
                                        </button>
                                    </div>
                                )}
                            >
                                {renderExpandedContent ? (
                                    renderExpandedContent(load)
                                ) : (
                                    <LoadExpandedRow
                                        load={load}
                                        originLocation={originLocationData}
                                        onLoadUpdate={onLoadUpdate}
                                        onEmailClick={onEmailClick}
                                        sendingEmail={sendingEmail}
                                        errorEmail={errorEmail}
                                        isOdd={isOdd}
                                        showInlineEmailChat={!onOpenThread}
                                        emailThreadId={emailThreadId}
                                        loadMongoId={loadMongoId}
                                        analyticsSurface={analyticsSurface}
                                    />
                                )}
                            </ErrorBoundary>
                            {isCancelled && (
                                <div className="absolute inset-0 z-10 flex items-center justify-center bg-red-50/90 dark:bg-red-950/80">
                                    <span className="rounded-full border border-red-200 bg-red-50 px-3 py-1 text-sm font-semibold text-red-700 shadow-sm dark:border-red-700 dark:bg-red-900/30 dark:text-red-300">
                                        This load has been cancelled
                                    </span>
                                </div>
                            )}
                        </div>
                    </td>
                </tr>
            )}
        </React.Fragment>
    )
}

/**
 * Re-render skip: when streaming a search, every AH_LoadAdded event creates a new `loads` array
 * reference in Redux. Without memoization, every existing row re-renders for every new load —
 * O(N) per event × N events = O(N²) work over a streaming burst. The comparator below returns
 * true (skip) unless one of the observable fields actually changed.
 *
 * Render-affecting fields enumerated explicitly. New fields the row paints MUST be added here.
 * Callback props are assumed stable from the parent (`ResultsView` wires them via useCallback).
 */
function areLoadRowPropsEqual(prev: LoadRowProps, next: LoadRowProps): boolean {
    if (prev.loadUid !== next.loadUid) return false
    if (prev.index !== next.index) return false
    if (prev.isExpanded !== next.isExpanded) return false
    // When expanded, the panel body comes from `renderExpandedContent`, which closes over the
    // live agent-load map — its identity changes when THIS row's data updates (e.g. a status flip
    // or scoring landing). Re-render so the open panel reflects it instead of going stale until a
    // manual collapse + re-expand. Cheap: only open rows re-run.
    if (next.isExpanded && prev.renderExpandedContent !== next.renderExpandedContent) return false
    if (prev.isSelected !== next.isSelected) return false
    if (prev.isViewed !== next.isViewed) return false
    if (prev.isEmailOpen !== next.isEmailOpen) return false
    if (prev.isNew !== next.isNew) return false
    if (prev.sendingEmail !== next.sendingEmail) return false
    if (prev.errorEmail !== next.errorEmail) return false
    if (prev.emailThreadId !== next.emailThreadId) return false
    // Voice-call lifecycle flags — agentic-hub mutates these per row as calls initiate,
    // go live, error, or persist post-call. Without these in the comparator, the contact
    // cell's email/phone icons → call-control buttons swap only renders when some OTHER
    // tracked prop (e.g. isExpanded) happens to change.
    if (prev.isCalling !== next.isCalling) return false
    if (prev.callError !== next.callError) return false
    if (prev.callInProgress !== next.callInProgress) return false
    if (prev.hasBeenCalled !== next.hasBeenCalled) return false
    if (prev.hasPhone !== next.hasPhone) return false
    if (prev.loadMongoId !== next.loadMongoId) return false
    if (prev.unreadCount !== next.unreadCount) return false
    if (prev.originLocation !== next.originLocation) return false
    if (prev.showRowNumber !== next.showRowNumber) return false
    if (prev.showStatusColumn !== next.showStatusColumn) return false
    if (prev.showStarColumn !== next.showStarColumn) return false
    if (prev.showCloseColumn !== next.showCloseColumn) return false
    if (prev.columnOrder !== next.columnOrder) return false

    // CanonicalLoad — compare the fields the row actually reads. Skip the rest.
    // CanonicalLoad fields (verified against this file's load.* usages):
    //   id, externalId, sourceType, bookingUrl, brokerTelegramAccountLink, isActive, brokerCompanyName, brokerContactEmail,
    //   brokerContactPhone, brokerMc, rateAmountUsd, ratePerMileUsd, distanceMiles,
    //   equipmentType, equipmentLengthFt, weightLbs, pickupCity, pickupStateProv,
    //   deliveryCity, deliveryStateProv, createdAt, details.{origin_deadhead_miles,
    //   destination_deadhead_miles}
    // Agentic-hub-specific status / streaming / pin state comes via rowMeta, not load.
    const a = prev.load
    const b = next.load
    if (a !== b) {
        if (
            a.externalId !== b.externalId ||
            a.id !== b.id ||
            a.isActive !== b.isActive ||
            a.sourceType !== b.sourceType ||
            a.bookingUrl !== b.bookingUrl ||
            a.brokerTelegramAccountLink !== b.brokerTelegramAccountLink ||
            a.brokerCompanyName !== b.brokerCompanyName ||
            a.brokerContactEmail !== b.brokerContactEmail ||
            a.brokerContactPhone !== b.brokerContactPhone ||
            a.brokerMc !== b.brokerMc ||
            a.rateAmountUsd !== b.rateAmountUsd ||
            a.ratePerMileUsd !== b.ratePerMileUsd ||
            a.distanceMiles !== b.distanceMiles ||
            a.equipmentType !== b.equipmentType ||
            a.equipmentLengthFt !== b.equipmentLengthFt ||
            a.weightLbs !== b.weightLbs ||
            a.pickupCity !== b.pickupCity ||
            a.pickupStateProv !== b.pickupStateProv ||
            a.deliveryCity !== b.deliveryCity ||
            a.deliveryStateProv !== b.deliveryStateProv
        ) {
            return false
        }
        // details has nested deadhead miles — compare scalars.
        const da = a.details
        const db = b.details
        if (da !== db) {
            if (!da || !db) return false
            if (da.origin_deadhead_miles !== db.origin_deadhead_miles || da.destination_deadhead_miles !== db.destination_deadhead_miles) return false
        }
    }

    // RowMeta — agentic-hub passes a fresh object each render. Compare its scalar fields.
    const ma = prev.rowMeta
    const mb = next.rowMeta
    if (ma !== mb) {
        if (!ma || !mb) return false
        if (ma.rowNumber !== mb.rowNumber) return false
        if (ma.status !== mb.status) return false
        if (ma.isPinned !== mb.isPinned) return false
        if (ma.bestFit !== mb.bestFit) return false
        if (ma.brokerReplied !== mb.brokerReplied) return false
        if (ma.hasPendingQuestion !== mb.hasPendingQuestion) return false
        if (ma.statusReason !== mb.statusReason) return false
        if (ma.restoredAt !== mb.restoredAt) return false
        if (ma.score !== mb.score) return false
        // Re-render when market intel lands late (the injected Destination cell reads it off
        // CanonicalSearchLoad, not the compared CanonicalLoad scalars) — see RowMeta.marketIntelKey.
        if (ma.marketIntelKey !== mb.marketIntelKey) return false
        // statusStyle is derived from status — compared by reference is enough since
        // the source (LOAD_STATUS_PALETTE) is a static module-level map.
        if (ma.statusStyle !== mb.statusStyle) return false
        const sa = ma.streamingState
        const sb = mb.streamingState
        if (sa !== sb) {
            if (!sa || !sb) return false
            if (sa.phase !== sb.phase || sa.error !== sb.error || sa.superseded !== sb.superseded) return false
            if (sa.toolName !== sb.toolName || sa.finalContent !== sb.finalContent) return false
        }
    }
    return true
}

export const LoadRow = React.memo(LoadRowImpl, areLoadRowPropsEqual)
